import { useOnChange } from "@prismicio/editor-support/React"
import {
  contentEditableClass,
  contentEditablePlaceholderClass,
} from "@prismicio/editor-ui"
import type { SharedSliceContent } from "@prismicio/types-internal/lib/content"
import { type Variation } from "@prismicio/types-internal/lib/customtypes"
import Placeholder from "@tiptap/extension-placeholder"
import { DOMSerializer } from "@tiptap/pm/model"
import { type AnyExtension, Editor, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useState } from "react"

import { fitHtmlIntoSlice } from "../../../../app/services/ai"
import {
  trackSmartCopyPasteDone,
  trackSmartCopyPasteStarted,
} from "../../../../app/services/tracking"
import { useDocument } from "../../../DocumentProvider"
import type {
  SliceConfigWithSharedSlice,
  VisualSharedSlice,
} from "../sliceTableData"
import { createSliceKey } from "../utils/createSliceKey"
import type { PagePreviewItem, PagePreviewItems } from "./PagePreview"

interface SmartCopyPasteArgs {
  slices: SliceConfigWithSharedSlice[]
  active: boolean
}

export function useSmartCopyPaste(args: SmartCopyPasteArgs) {
  const { slices, active } = args

  const { data, documentId } = useDocument()

  const editor = useEditor(
    {
      autofocus: true,
      editorProps: {
        attributes: {
          class: `${contentEditableClass} ${contentEditablePlaceholderClass}`,
          style: "height: 100%",
        },
      },
      extensions,
    },
    [active],
  )

  const [items, setItems] = useState<PagePreviewItems>({})

  // Clear the items when the dialog is closed.
  useOnChange(active, () => {
    if (active) return
    setItems({})
  })

  // Only keep the items from the current smart copy paste session.
  function setKnownItem(args: { uuid: string; item: PagePreviewItem }) {
    const { uuid, item } = args
    setItems((items) => {
      if (!items[uuid]) return items
      return {
        ...items,
        [uuid]: item,
      }
    })
  }

  function fitSelectionIntoSlice(slice: SliceConfigWithSharedSlice) {
    const variation = slice.sliceConfig.variations.find(
      (variation) => variation.id === slice.sliceVariation,
    )
    if (!variation) return

    const html = getSelectedHTML(editor)
    if (!html) return

    const uuid = crypto.randomUUID()
    // Delete the selection, then delete the empty node that is left behind.
    editor?.chain().deleteSelection().deleteCurrentNode().blur().run()

    setItems((items) => ({
      ...items,
      [uuid]: { status: "loading", slice },
    }))

    const trackingInfo = {
      customTypeId: data.customType.id,
      ...(documentId && { documentId }),
      sliceId: slice.sliceConfig.id,
      variationId: variation.id,
    }
    trackSmartCopyPasteStarted(trackingInfo)
    const started = Date.now()

    fitHtmlIntoSlice({
      uuid,
      slices: slices.map((slice) => slice.sliceConfig),
      slice: slice.sliceConfig.id,
      variation: variation.id,
      html,
      onMessage: (message) => {
        switch (message.status) {
          case "fitting":
            break
          case "done":
            setKnownItem({
              uuid,
              item: {
                status: "done",
                slice: getVisualSharedSlice({
                  slice,
                  variation,
                  content: message.slice,
                }),
              },
            })
            trackSmartCopyPasteDone({
              ...trackingInfo,
              cost: String(message.cost),
              time: ((Date.now() - started) / 1000).toFixed(1),
              distance: String(message.distance),
            })
            break
          case "failed":
            setKnownItem({
              uuid,
              item: { status: "failed", slice, variation, html },
            })
            break
        }
      },
    })
  }

  function retry(args: { uuid: string }) {
    const { uuid } = args

    const item = items[uuid]
    if (!item || item.status !== "failed") return

    const { slice, variation, html } = item

    setItems((items) => ({
      ...items,
      [uuid]: { status: "loading", slice },
    }))

    const trackingInfo = {
      customTypeId: data.customType.id,
      ...(documentId && { documentId }),
      sliceId: slice.sliceConfig.id,
      variationId: variation.id,
      retried: "true" as const,
    }
    trackSmartCopyPasteStarted(trackingInfo)
    const started = Date.now()

    // We reuse the previous uuid to keep the position in the list.
    // For the request, we always use a new one.
    fitHtmlIntoSlice({
      uuid: crypto.randomUUID(),
      slices: slices.map((slice) => slice.sliceConfig),
      slice: slice.sliceConfig.id,
      variation: variation.id,
      html,
      onMessage: (message) => {
        switch (message.status) {
          case "fitting":
            break
          case "done":
            setKnownItem({
              uuid,
              item: {
                status: "done",
                slice: getVisualSharedSlice({
                  slice,
                  variation,
                  content: message.slice,
                }),
              },
            })
            trackSmartCopyPasteDone({
              ...trackingInfo,
              cost: String(message.cost),
              time: ((Date.now() - started) / 1000).toFixed(1),
              distance: String(message.distance),
            })
            break
          case "failed":
            setKnownItem({
              uuid,
              item: { status: "failed", slice, variation, html },
            })
            break
        }
      },
    })
  }

  return { editor, fitSelectionIntoSlice, items, retry }
}

const extensions = [
  StarterKit,
  Placeholder.configure({
    placeholder:
      "Paste content from elsewhere (e.g., Notion, Google Docs) here",
  }),
] as AnyExtension[]

function getSelectedHTML(editor: Editor | null) {
  // The editor doesn't have way to provide its selected content as HTML.
  // As a workaround, a throwaway DOM element is created with a serialization
  // of the selected content, and its innerHTML is returned.

  if (!editor) return undefined

  const { selection } = editor.state
  if (selection.empty) return undefined

  const content = selection.content()
  const fragment = DOMSerializer.fromSchema(
    editor.state.schema,
  ).serializeFragment(content.content)

  const div = document.createElement("div")
  div.appendChild(fragment)
  return div.innerHTML
}

interface GetVisualSharedSliceArgs {
  slice: SliceConfigWithSharedSlice
  variation: Variation
  content: SharedSliceContent
}

function getVisualSharedSlice(
  args: GetVisualSharedSliceArgs,
): VisualSharedSlice {
  const { slice, variation, content } = args

  const uuid = crypto.randomUUID()
  const key = createSliceKey({ sliceId: slice.sliceConfig.id, uuid })

  return {
    type: "shared",
    zoneId: slice.zoneId,
    config: slice.sliceConfig,
    name: slice.sliceReference,
    variation,
    content,
    uuid,
    key,
    canMoveUp: false,
    canMoveDown: false,
  }
}
